import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const CIRCLE = 192;
const OUTPUT = 512;

interface AvatarCropPreviewProps {
  sourceUrl: string;
  onAccept: (croppedBlob: Blob) => void;
  onRetake: () => void;
}

export const AvatarCropPreview: React.FC<AvatarCropPreviewProps> = ({
  sourceUrl,
  onAccept,
  onRetake,
}) => {
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragStartRef = useRef<{
    pointerX: number;
    pointerY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Load source image natural dimensions.
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = sourceUrl;
  }, [sourceUrl]);

  // Reset pan when a new source arrives.
  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [sourceUrl]);

  const baseScale = imgSize ? CIRCLE / Math.min(imgSize.w, imgSize.h) : 1;
  const scale = baseScale;
  const displayW = imgSize ? imgSize.w * scale : 0;
  const displayH = imgSize ? imgSize.h * scale : 0;

  const clampPan = (p: { x: number; y: number }) => {
    const maxX = Math.max(0, (displayW - CIRCLE) / 2);
    const maxY = Math.max(0, (displayH - CIRCLE) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, p.x)),
      y: Math.max(-maxY, Math.min(maxY, p.y)),
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    dragStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.pointerX;
    const dy = e.clientY - dragStartRef.current.pointerY;
    setPan(
      clampPan({
        x: dragStartRef.current.panX + dx,
        y: dragStartRef.current.panY + dy,
      })
    );
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
    dragStartRef.current = null;
    setDragging(false);
  };

  const handleAccept = () => {
    if (!imgSize) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const sizeInImage = CIRCLE / scale;
      const cxImg = imgSize.w / 2 - pan.x / scale;
      const cyImg = imgSize.h / 2 - pan.y / scale;
      const sx = cxImg - sizeInImage / 2;
      const sy = cyImg - sizeInImage / 2;
      ctx.drawImage(img, sx, sy, sizeInImage, sizeInImage, 0, 0, OUTPUT, OUTPUT);
      canvas.toBlob(
        (blob) => {
          if (blob) onAccept(blob);
        },
        'image/jpeg',
        0.9
      );
    };
    img.src = sourceUrl;
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black/80 flex items-center justify-center p-6">
      <div className="bg-[#2A2824] rounded-3xl p-6 w-full max-w-sm">
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            width: CIRCLE,
            height: CIRCLE,
            borderRadius: '50%',
            overflow: 'hidden',
            position: 'relative',
            margin: '0 auto 16px',
            touchAction: 'none',
            cursor: dragging ? 'grabbing' : 'grab',
            background: '#1A1814',
            userSelect: 'none',
          }}
        >
          {imgSize && (
            <img
              src={sourceUrl}
              alt="Preview"
              draggable={false}
              style={{
                position: 'absolute',
                width: displayW,
                height: displayH,
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px)`,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          )}
        </div>

        <p className="text-white/60 text-center text-[13px] mb-4">
          Drag to reposition
        </p>

        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            disabled={!imgSize}
            className="flex-1 bg-[#D4715A] text-white rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
            Use Photo
          </button>
          <button
            onClick={onRetake}
            className="flex-1 bg-white/15 text-white rounded-full py-3.5 font-semibold"
          >
            Retake
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AvatarCropPreview;
